import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Plus, 
  Download, 
  Calendar, 
  DollarSign, 
  User, 
  Clock, 
  TrendingUp,
  CheckCircle,
  AlertCircle,
  CreditCard,
  Shield,
  Check,
  X,
  Send,
  FileText,
  Users,
  MessageSquare,
  RefreshCw
} from 'lucide-react';
import { useTableBankingLoanRepayment, useTableBankingLoanRepayments,useLoanRepaymentsByLoan, usePayLoanViaMpesa } from '../hooks/useTableBankingLoanRepayments.ts';
import { useIsAdmin } from '../hooks/useIsAdmin.ts';
import { useGetProfile } from '../hooks/useProfile.ts';
import Modal from '../components/Modal.tsx';
import ExportFormatModal from '../components/ExportFormatModal.tsx';
import LoanRepaymentTimeline from '../components/LoanRepaymentTimeline.tsx';
import LoanStatusTimeline from '../components/LoanStatusTimeline.js';
import { formatCurrency, PAYMENT_METHOD_OPTIONS } from '../hooks/useLoanRepayments.ts';
import { toast } from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import getApiErrorMessage from '../utils/helper.js';
import api from '../utils/axios.ts';
import { PermissionPage } from '../components/PermissionGate';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    PENDING: { label: 'Pending Approval', color: 'yellow' },
    APPROVED: { label: 'Approved', color: 'green' },
    DISAPPROVED: { label: 'Disapproved', color: 'red' },
    ACTIVE: { label: 'Active', color: 'blue' },
    PAID: { label: 'Paid', color: 'gray' },
  };
  
  const config = statusConfig[status] || { label: status, color: 'gray' };
  
  const colorClasses = {
    yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    green: 'bg-green-100 text-green-800 border-green-200',
    red: 'bg-red-100 text-red-800 border-red-200',
    blue: 'bg-blue-100 text-blue-800 border-blue-200',
    gray: 'bg-gray-100 text-gray-800 border-gray-200',
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${colorClasses[config.color]}`}>
      {config.label}
    </span>
  );
};

export default function TableBankingLoanDetailsPage() {
  const { loanId } = useParams();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const { can } = usePermissions();
  const { data: userProfile } = useGetProfile();
  const queryClient = useQueryClient();
  
  const [isRepaymentModalOpen, setIsRepaymentModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isMpesaModalOpen, setIsMpesaModalOpen] = useState(false);
  const [selectedRepayment, setSelectedRepayment] = useState(null);
  
  // Approval workflow states
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [approvalAction, setApprovalAction] = useState(null); // 'approve' or 'disapprove'
  const [approvalNotes, setApprovalNotes] = useState('');
  const [isApprovalLoading, setIsApprovalLoading] = useState(false);
  const [disbursementModalOpen, setDisbursementModalOpen] = useState(false);
  const [disbursementNotes, setDisbursementNotes] = useState('');
  const [disbursementDate, setDisbursementDate] = useState('');
  const [sendSmsNotification, setSendSmsNotification] = useState(true);
  const [isDisbursementLoading, setIsDisbursementLoading] = useState(false);
  const [showApprovalTimeline, setShowApprovalTimeline] = useState(false);
  const [showRepaymentExportModal, setShowRepaymentExportModal] = useState(false);
  
  // Fetch loan and repayment data
  const { data: loanRepaymentsData, isLoading: repaymentsLoading } = useLoanRepaymentsByLoan(parseInt(loanId));
  // const createRepayment = useCreateRepayment();
  // const updateRepayment = useUpdateRepayment();
  // const deleteRepayment = useDeleteRepayment();
  const payLoanViaMpesa = usePayLoanViaMpesa();
  // const exportLoanRepayments = useExportLoanRepayments();

  

  const loanData = loanRepaymentsData?.data?.loan;
  const repayments = loanRepaymentsData?.data?.repayments || [];
  const summary = loanRepaymentsData?.data?.summary;

  // Approval workflow functions
  const handleApprovalAction = (action) => {
    setApprovalAction(action);
    setApprovalModalOpen(true);
    setApprovalNotes('');
  };

  const submitApproval = async () => {
    if (!approvalAction) return;

    const id = parseInt(String(loanId), 10);
    if (!Number.isFinite(id) || id < 1) {
      toast.error('Invalid loan. Go back to the list and open this loan again.');
      return;
    }

    setIsApprovalLoading(true);
    try {
      const endpoint =
        approvalAction === 'approve'
          ? `/tablebanking/approve/${id}`
          : `/tablebanking/disapprove/${id}`;
      const payload = {
        reason: approvalNotes,
        send_sms: true,
      };

      await api.post(endpoint, payload);
      
      toast.success(`Loan ${approvalAction === 'approve' ? 'approved' : 'disapproved'} successfully`);
      
      // Refresh data
      queryClient.invalidateQueries(['loanRepaymentsByLoan']);
      queryClient.invalidateQueries(['tableBankingLoans']);
      
      setApprovalModalOpen(false);
      setApprovalAction(null);
      setApprovalNotes('');
    } catch (error) {
      const errorMessage = getApiErrorMessage(error);
      toast.error(errorMessage || `Failed to ${approvalAction} loan`);
    } finally {
      setIsApprovalLoading(false);
    }
  };

  const handleDisbursement = () => {
    setDisbursementModalOpen(true);
    setDisbursementNotes('');
    setDisbursementDate(new Date().toISOString().slice(0, 19).replace('T', ' '));
    setSendSmsNotification(true);
  };

  const submitDisbursement = async () => {
    setIsDisbursementLoading(true);
    try {
      const payload = {
        disbursement_notes: disbursementNotes,
        disbursed_at: disbursementDate,
        send_sms: sendSmsNotification,
      };

      await api.post(`/tablebanking/disburse/${loanId}`, payload);
      
      toast.success('Loan disbursed successfully');
      
      // Refresh data
      queryClient.invalidateQueries(['loanRepaymentsByLoan']);
      queryClient.invalidateQueries(['tableBankingLoans']);
      
      setDisbursementModalOpen(false);
      setDisbursementNotes('');
      setDisbursementDate('');
      setSendSmsNotification(true);
    } catch (error) {
      const errorMessage = getApiErrorMessage(error);
      toast.error(errorMessage || 'Failed to disburse loan');
    } finally {
      setIsDisbursementLoading(false);
    }
  };

  // const handleCreateRepayment = (repaymentData) => {
  //   createRepayment.mutate(repaymentData, {
  //     onSuccess: () => {
  //       setIsRepaymentModalOpen(false);
  //     }
  //   });
  // };

  const handleEditRepayment = (repayment) => {
    setSelectedRepayment(repayment);
    setIsEditModalOpen(true);
  };

  // const handleUpdateRepayment = (repaymentData) => {
  //   updateRepayment.mutate({ id: selectedRepayment.id, ...repaymentData }, {
  //     onSuccess: () => {
  //       setIsEditModalOpen(false);
  //       setSelectedRepayment(null);
  //     }
  //   });
  // };

  // const handleDeleteRepayment = (repaymentId) => {
  //   if (window.confirm('Are you sure you want to delete this repayment? This action cannot be undone.')) {
  //     deleteRepayment.mutate(repaymentId);
  //   }
  // };

  const handleMpesaPayment = (paymentData) => {
    payLoanViaMpesa.mutate(paymentData, {
      onSuccess: () => {
        setIsMpesaModalOpen(false);
      }
    });
  };



  if (repaymentsLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading loan details...</span>
        </div>
      </div>
    );
  }

  if (!loanData) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <AlertCircle size={48} className="mx-auto text-red-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-700 mb-2">Loan Not Found</h3>
          <p className="text-gray-500 mb-4">The requested loan could not be found.</p>
          <button
            onClick={() => navigate('/member-loans')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Back to Loans
          </button>
        </div>
      </div>
    );
  }

  const progressPercentage = loanData.total_amount > 0 && summary?.total_repaid
    ? (summary.total_repaid / loanData.total_amount) * 100 
    : 0;

  const isFullyPaid = loanData.loan_balance <= 0;

  

  // Check if loan can be approved/disbursed
  const canBeApproved = loanData.status === 'pending';
  const canBeDisbursed = loanData.status === 'approved';

  return (
    <PermissionPage permission={PERMISSIONS.VIEW_ALL_LOANS}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/table-banking-loans')}
              className="mr-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Loan Details</h1>
              <p className="text-gray-600 mt-1">
                {loanData.member?.name} • {loanData.loan_type?.name}
              </p>
            </div>
          </div>
          
          <div className="flex gap-2">
            {/* Approval workflow actions for admins */}
            {isAdmin && loanData.status === 'pending' && (
              <>
                {can(PERMISSIONS.APPROVE_LOAN) && (
                  <div className="relative group">
                    <button 
                      onClick={() => handleApprovalAction('approve')}
                      // disabled={!guarantorRequirementsMet()}
                      className={`flex items-center px-4 py-2 rounded-lg`}
                      title={"Approve Loan"}
                    >
                      <Check size={16} className="mr-2" />
                      Approve
                    </button>
                  </div>
                )}
                {can(PERMISSIONS.REJECT_LOAN) && (
                  <button 
                    onClick={() => handleApprovalAction('disapprove')}
                    className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    title="Disapprove Loan"
                  >
                    <X size={16} className="mr-2" />
                    Disapprove
                  </button>
                )}
              </>
            )}
          
          {isAdmin && canBeDisbursed && (
            <button 
              onClick={handleDisbursement}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              title="Disburse Loan"
            >
              <Send size={16} className="mr-2" />
              Disburse
            </button>
          )}
          
          {/* Send Guarantor Requests - Only for loan applicant when loan is pending and requires guarantors */}
         
          
          {/* Reapply Loan Button - Only for admins when loan can be reapplied */}

          {!isFullyPaid && (
            <>
              <div className="relative group">
                <button 
                  onClick={() => {
                    if (userProfile?.data?.mpesa_credentials) {
                      setIsMpesaModalOpen(true);
                    }
                  }}
                  className={`flex items-center px-4 py-2 rounded-lg ${
                    userProfile?.data?.mpesa_credentials 
                      ? 'bg-green-600 text-white hover:bg-green-700' 
                      : 'bg-gray-400 text-white cursor-not-allowed'
                  }`}
                  disabled={!userProfile?.data?.mpesa_credentials}
                >
                  <CreditCard size={16} className="mr-2" />
                  Pay via M-Pesa
                </button>

                {/* Tooltip */}
                {!userProfile?.data?.mpesa_credentials && (
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg z-50 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                    <div className="flex items-center">
                      <AlertCircle size={14} className="mr-1" />
                      M-Pesa integration not configured
                    </div>
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                  </div>
                )}
              </div>
              {/* {isAdmin && (
                <button 
                  onClick={() => setIsRepaymentModalOpen(true)}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Plus size={16} className="mr-2" />
                  Add Payment
                </button>
              )} */}
            </>
          )}
        </div>
      </div>

      {/* Loan Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 kpi-grid">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Principal Amount</p>
              <h3 className="text-2xl font-bold">{formatCurrency(loanData.principal_amount)}</h3>
            </div>
            <DollarSign size={32} className="text-blue-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Total Repaid</p>
              <h3 className="text-2xl font-bold">{formatCurrency(summary?.total_repaid ?? 0)}</h3>
            </div>
            <CheckCircle size={32} className="text-green-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm">Outstanding Balance</p>
              <h3 className="text-2xl font-bold">{formatCurrency(loanData.loan_balance)}</h3>
            </div>
            <TrendingUp size={32} className="text-orange-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Progress</p>
              <h3 className="text-2xl font-bold">{Math.round(progressPercentage)}%</h3>
            </div>
            <Clock size={32} className="text-purple-200" />
          </div>
        </div>
      </div>

   
      {/* Loan Information Panel */}
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Loan Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Borrower</label>
              <div className="flex items-center mt-1">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                  <User size={16} className="text-blue-600" />
                </div>
                <div>
                  <p className="font-medium">{loanData.member?.name}</p>
                  <p className="text-sm text-gray-500">{loanData.member?.phone_number}</p>
                </div>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-500">Loan Type</label>
              <p className="mt-1 font-medium">{loanData.loan_type?.name}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Repayment Date</label>
              <p className="mt-1 font-medium">{new Date(loanData.repayment_date).toLocaleDateString()}</p>
            </div>
            
    
            
            <div>
              <label className="text-sm font-medium text-gray-500">Duration days</label>
              <p className="mt-1 font-medium">{loanData.table_banking.repayment_period || `${loanData.table_banking.repayment_period} days`}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Interest Rate </label>
              <p className="mt-1 font-medium">{loanData.table_banking.interest_rate}% {loanData.rate_period_label || 'per month'}</p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-500">Total Amount</label>
              <p className="mt-1 font-medium">{formatCurrency(loanData.total_amount)}</p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-500">Status</label>
              <div className="mt-1">
                <StatusBadge status={loanData.status} />
              </div>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-6">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Repayment Progress</span>
            <span>{Math.round(progressPercentage)}% Complete</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className={`h-3 rounded-full transition-all duration-1000 ease-out ${
                isFullyPaid ? 'bg-gradient-to-r from-green-500 to-green-600' : 'bg-gradient-to-r from-blue-500 to-purple-500'
              }`}
              style={{ width: `${Math.min(progressPercentage, 100)}%` }}
            />
          </div>
        </div>

       

        
      </div>

      {/* Approval Workflow Information */}
      {isAdmin && (
        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Shield size={20} className="mr-2 text-gray-600" />
            Approval Workflow
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Applied By</label>
                <div className="flex items-center mt-1">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                    <Users size={16} className="text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium">{loanData.applied_by?.name || 'N/A'}</p>
                    <p className="text-sm text-gray-500">
                      {loanData.created_at ? new Date(loanData.created_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Approved By</label>
                <div className="flex items-center mt-1">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <CheckCircle size={16} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium">{loanData.approved_by?.name || 'N/A'}</p>
                    <p className="text-sm text-gray-500">
                      {loanData?.approved_by?.created_at ? new Date(loanData.approved_by.created_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
              
              {loanData.approval_notes && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Approval Notes</label>
                  <p className="mt-1 text-sm bg-gray-50 p-2 rounded">{loanData.approval_notes}</p>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Disbursed By</label>
                <div className="flex items-center mt-1">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                    <Send size={16} className="text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium">{loanData.disbursed_by?.name || 'N/A'}</p>
                    <p className="text-sm text-gray-500">
                      {loanData.disbursed_at ? new Date(loanData.disbursed_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
              
              {loanData.disbursement_notes && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Disbursement Notes</label>
                  <p className="mt-1 text-sm bg-gray-50 p-2 rounded">{loanData.disbursement_notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    

      



    

      {/* Approval Timeline Modal */}
      {showApprovalTimeline && (
        <Modal 
          isOpen={showApprovalTimeline} 
          onClose={() => setShowApprovalTimeline(false)}
          title="Loan Approval Timeline"
        >
          <LoanStatusTimeline 
            loanId={loanId} 
            isAdmin={isAdmin} 
            onClose={() => setShowApprovalTimeline(false)}
          />
        </Modal>
      )}


      {/* M-Pesa Payment Modal */}
      <Modal 
        isOpen={isMpesaModalOpen} 
        onClose={() => setIsMpesaModalOpen(false)}
        title="Pay via M-Pesa"
      >
        <MpesaPaymentForm
          loanData={loanData}
          userProfile={userProfile}
          onSubmit={handleMpesaPayment}
          onCancel={() => setIsMpesaModalOpen(false)}
          isLoading={payLoanViaMpesa.isLoading}
        />
      </Modal>

      {/* Approval Action Modal */}
      <Modal 
        isOpen={approvalModalOpen} 
        onClose={() => setApprovalModalOpen(false)}
        title={`${approvalAction === 'approve' ? 'Approve' : 'Disapprove'} Loan`}
      >
        <div className="space-y-4">
          {loanData && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Loan Details</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Member:</span>
                  <p className="font-medium">{loanData.member?.name}</p>
                </div>
                <div>
                  <span className="text-gray-600">Amount:</span>
                  <p className="font-medium">{formatCurrency(loanData.principal)}</p>
                </div>
                <div>
                  <span className="text-gray-600">Loan Type:</span>
                  <p className="font-medium">{loanData.loan_type?.name}</p>
                </div>
              
              </div>
            </div>
          )}
          
          <p className="text-sm text-gray-700">
            Are you sure you want to <strong>{approvalAction}</strong> this loan? This action cannot be undone.
          </p>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              rows="4"
              placeholder="Add any notes for the approval/disapproval..."
              value={approvalNotes}
              onChange={(e) => setApprovalNotes(e.target.value)}
            />
          </div>
          
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
              onClick={() => setApprovalModalOpen(false)}
              disabled={isApprovalLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`px-4 py-2 rounded text-white hover:opacity-90 ${
                approvalAction === 'approve' 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'bg-red-600 hover:bg-red-700'
              }`}
              onClick={submitApproval}
              disabled={isApprovalLoading}
            >
              {isApprovalLoading ? 'Processing...' : `${approvalAction === 'approve' ? 'Approve' : 'Disapprove'} Loan`}
            </button>
          </div>
        </div>
      </Modal>

      {/* Disbursement Modal */}
      <Modal 
        isOpen={disbursementModalOpen} 
        onClose={() => setDisbursementModalOpen(false)}
        title="Disburse Loan"
      >
        <div className="space-y-4">
          {loanData && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Loan Details</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Member:</span>
                  <p className="font-medium">{loanData.member?.name}</p>
                </div>
                <div>
                  <span className="text-gray-600">Amount:</span>
                  <p className="font-medium">{formatCurrency(loanData.principal_amount)}</p>
                </div>
              </div>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Disbursement Date *
            </label>
            <input
              type="datetime-local"
              value={disbursementDate.replace(' ', 'T')}
              onChange={(e) => setDisbursementDate(e.target.value.replace('T', ' '))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={sendSmsNotification}
              onChange={(e) => setSendSmsNotification(e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm text-gray-700">Send SMS notification to borrower</span>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (optional)
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              rows="4"
              placeholder="Additional notes for disbursement..."
              value={disbursementNotes}
              onChange={(e) => setDisbursementNotes(e.target.value)}
            />
          </div>
          
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
              onClick={() => setDisbursementModalOpen(false)}
              disabled={isDisbursementLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
              onClick={submitDisbursement}
              disabled={isDisbursementLoading}
            >
              {isDisbursementLoading ? 'Processing...' : 'Disburse Loan'}
            </button>
          </div>
        </div>
      </Modal>
      
      
    </div>
    </PermissionPage>
  );
}

function EditRepaymentForm({ loanData, initialData, onSubmit, onCancel }) {
  const [amount, setAmount] = useState(initialData?.amount || '');
  const [paymentDate, setPaymentDate] = useState(initialData?.payment_date || '');
  const [paymentMethod, setPaymentMethod] = useState(initialData?.payment_method || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [transactionReference, setTransactionReference] = useState(initialData?.transaction_reference || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      amount: parseFloat(amount),
      payment_date: paymentDate,
      payment_method: paymentMethod,
      description: description,
      transaction_reference: transactionReference,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
        <input
          type="number"
          step="0.01"
          min="0"
          max={loanData.loan_balance}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          required
        />
        <p className="text-xs text-gray-500 mt-1">
          Outstanding balance: {formatCurrency(loanData.loan_balance)}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date *</label>
        <input
          type="date"
          value={paymentDate}
          onChange={(e) => setPaymentDate(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method *</label>
        <select
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          required
        >
          <option value="">Select payment method</option>
          {PAYMENT_METHOD_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows="3"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          placeholder="Additional notes about this payment..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Reference</label>
        <input
          type="text"
          value={transactionReference}
          onChange={(e) => setTransactionReference(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          placeholder="e.g., MPESA transaction ID"
        />
      </div>

      <div className="flex justify-end space-x-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
        >
          Update Payment
        </button>
      </div>
    </form>
  );
}

function RepaymentForm({ loanData, onSubmit, onCancel }) {
  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [description, setDescription] = useState('');
  const [transactionReference, setTransactionReference] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      table_banking_loan_id: loanData.id,
      amount: parseFloat(amount),
      payment_date: paymentDate,
      payment_method: paymentMethod,
      description: description,
      transaction_reference: transactionReference,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
        <input
          type="number"
          step="0.01"
          min="0"
          max={loanData.loan_balance}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          required
        />
        <p className="text-xs text-gray-500 mt-1">
          Outstanding balance: {formatCurrency(loanData.loan_balance)}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date *</label>
        <input
          type="date"
          value={paymentDate}
          onChange={(e) => setPaymentDate(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method *</label>
        <select
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          required
        >
          <option value="">Select payment method</option>
          {PAYMENT_METHOD_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows="3"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          placeholder="Additional notes about this payment..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Reference</label>
        <input
          type="text"
          value={transactionReference}
          onChange={(e) => setTransactionReference(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          placeholder="e.g., MPESA transaction ID"
        />
      </div>

      <div className="flex justify-end space-x-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
        >
          Add Payment
        </button>
      </div>
    </form>
  );
}

function getStatusColor(status) {
  const colors = {
    'PENDING': 'bg-yellow-100 text-yellow-800',
    'APPROVED': 'bg-green-100 text-green-800',
    'DISAPPROVED': 'bg-red-100 text-red-800',
    'ACTIVE': 'bg-blue-100 text-blue-800',
    'PAID': 'bg-gray-100 text-gray-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
}






function MpesaPaymentForm({ loanData, userProfile, onSubmit, onCancel, isLoading }) {
  const [phoneNumber, setPhoneNumber] = useState(userProfile?.data?.phone_number || '');
  const [amount, setAmount] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      table_banking_loan_id: loanData.id,
      phone_number: phoneNumber,
      amount: parseFloat(amount),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
        <input
          type="tel"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          placeholder="254700000000"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
        <input
          type="number"
          step="0.01"
          min="0"
          max={loanData.loan_balance}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          required
        />
        <p className="text-xs text-gray-500 mt-1">
          Outstanding balance: {formatCurrency(loanData.loan_balance)}
        </p>
      </div>

      <div className="flex justify-end space-x-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700"
          disabled={isLoading}
        >
          {isLoading ? 'Processing...' : 'Pay via M-Pesa'}
        </button>
      </div>
    </form>
  );
} 